/**
 * GarmentLabel Component
 *
 * 4x3" dual-code label for POD garments.
 * QR top-left (Production App), barcode bottom-right (AutoPrint).
 *
 * Layout from QR-Spec.md POD Label Layout section.
 *
 * V1 SCOPE:
 * - DTG production method only (DTF deferred to V2)
 * - POD and P&P fulfillment types
 * - Pretreat (PT) badge for dark garments
 *
 * V2 ADDITIONS (Phase 19):
 * - Template-based rendering from label layout editor
 * - Falls back to legacy hardcoded layout if no template
 * - useLegacyLayout prop to force legacy mode
 */

import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import { formatOrderNumber, stripOrderNamePrefix } from '@threadheads/shared';
import { encodeGarmentQR, CURRENT_QR_VERSION, type GarmentQRPayload } from '../../utils/qr-encoder';
import {
  BARCODE_CONFIG,
  isValidBarcodeContent,
  sanitizeBarcodeContent,
} from '../../utils/barcode-config';
import {
  LABEL_DIMENSIONS,
  QR_CONFIG,
  LABEL_COLORS,
} from '../../utils/label-constants';
import { useLabelTemplate, useLabelTemplateById, type LabelTemplate } from '../../hooks/useLabelTemplate';
import {
  getCanvasPixelSize,
  getComponentPixelBounds,
  getVisibleComponents,
  type GarmentData,
} from '../../utils/label-renderer';
import type { ComponentType } from '@/features/production-settings/components/label-layout/label-layout-types';
import { PRINT_TECHNOLOGY_SWITCH_REASON } from '@/features/production-tracker/lib/print-technology-switch';
import { CLIENT_QC_REDO_REASON } from '@/features/production-tracker/lib/client-qc-redo';
import { printAreaFontSize } from '../../utils/print-area-fit';

// ============================================================================
// TYPES
// ============================================================================

export interface GarmentLabelProps {
  garment: {
    id: string;              // garment_unit_id
    orderNumber: string;     // e.g., "TH-104923"
    sku: string;             // e.g., "TSHIRT-BLK-001" (normalized)
    shopifySku?: string | null; // e.g., "HT-XS-BK-LAB5D-R7" (original from Shopify, for barcode)
    productName: string;     // e.g., "Classic Tee"
    size: string;            // e.g., "XL"
    color: string;           // e.g., "BLACK"
    requiresPretreat: boolean;
    fulfillmentType: 'POD' | 'P&P';
    itemNumber?: number;     // e.g., 2 of 4
    totalItems?: number;
    shopifyProductType?: string | null; // e.g., "T-Shirt", "Hoodie"
    // Phase 50.01.3 / BLU-233: drives the QR `m` field + the DTF/DTG label
    // indicator. Optional — defaults to 'DTG' when absent.
    printTechnology?: 'DTG' | 'DTF';
    /**
     * Phase 104 (D-09): where each print goes on a manual order's garment,
     * e.g. ['FRONT LEFT CHEST', 'BACK FULL BACK'] (from getBatchLabels /
     * getGarmentLabel). Rendered by the template's 'printArea' component;
     * empty or absent renders nothing.
     */
    printArea?: readonly string[];
  };
  binLabel?: string;         // e.g., "BIN-042"
  isRedo?: boolean;
  redoReason?: string;       // e.g., "Print defect"
  /** Override with specific template ID */
  templateId?: string;
  /** Force legacy hardcoded layout (skip template lookup) */
  useLegacyLayout?: boolean;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * 4x3" garment label with dual codes.
 *
 * CRITICAL from QR-Spec.md:
 * - QR Code: Top-left corner (for Production App handheld scanner)
 * - Barcode: Bottom-right corner (for AutoPrint keyboard wedge scanner)
 * - Codes on OPPOSITE corners to prevent accidental wrong scan
 *
 * Template-based rendering:
 * - Loads appropriate template based on fulfillment type
 * - Falls back to legacy layout if no template exists
 * - useLegacyLayout prop skips template lookup entirely
 */
export function GarmentLabel({
  garment,
  binLabel,
  isRedo = false,
  redoReason,
  templateId,
  useLegacyLayout = false,
}: GarmentLabelProps) {
  // Load template by ID if provided
  const byIdResult = useLabelTemplateById(templateId ?? null);

  // Load template based on fulfillment type as fallback
  const byTypeResult = useLabelTemplate(garment.fulfillmentType);

  // Use template by ID if provided and found, otherwise fall back to by-type
  const template = templateId ? byIdResult.template : byTypeResult.template;
  const isLoading = templateId ? byIdResult.isLoading : byTypeResult.isLoading;

  // Always use legacy layout if:
  // 1. useLegacyLayout prop is true
  // 2. Template is still loading (avoid flash)
  // 3. No template exists
  const shouldUseLegacy = useLegacyLayout || isLoading || !template;

  if (shouldUseLegacy) {
    return (
      <LegacyGarmentLabel
        garment={garment}
        binLabel={binLabel}
        isRedo={isRedo}
        redoReason={redoReason}
      />
    );
  }

  // Template-based rendering
  return (
    <TemplateBasedLabel
      garment={garment}
      binLabel={binLabel}
      isRedo={isRedo}
      template={template}
    />
  );
}

// ============================================================================
// TEMPLATE-BASED LABEL
// ============================================================================

/** Print-time garment data: the shared shape plus the manual print areas (D-09). */
type LabelGarmentData = GarmentData & { printArea?: readonly string[] };

interface TemplateBasedLabelProps {
  garment: GarmentLabelProps['garment'];
  binLabel?: string;
  isRedo: boolean;
  template: LabelTemplate;
}

/**
 * Renders a label using the template layout.
 * Components are positioned absolutely based on template config.
 */
function TemplateBasedLabel({
  garment,
  binLabel,
  isRedo,
  template,
}: TemplateBasedLabelProps) {
  const { canvasSize, components } = template;
  const canvasPx = getCanvasPixelSize(canvasSize);
  const visibleComponents = getVisibleComponents(components);

  // Map garment data to GarmentData format
  const garmentData: LabelGarmentData = {
    id: garment.id,
    orderNumber: garment.orderNumber,
    sku: garment.sku,
    shopifySku: garment.shopifySku,
    productName: garment.productName,
    size: garment.size,
    color: garment.color,
    requiresPretreat: garment.requiresPretreat,
    fulfillmentType: garment.fulfillmentType,
    itemNumber: garment.itemNumber ?? 1,
    totalItems: garment.totalItems ?? 1,
    binLabel: binLabel ?? '',
    isRedo,
    shopifyProductType: garment.shopifyProductType ?? null,
    printTechnology: garment.printTechnology ?? 'DTG',
    printArea: garment.printArea,
  };

  return (
    <div
      className="label"
      data-testid="garment-label"
      data-garment-id={garment.id}
      data-template-id={template.id}
      style={{
        width: canvasPx.width,
        height: canvasPx.height,
        position: 'relative',
        backgroundColor: 'white',
        fontFamily: '"PingFang SC", "Microsoft YaHei", "Noto Sans SC", Arial, "Helvetica Neue", sans-serif',
        boxSizing: 'border-box',
      }}
    >
      {visibleComponents.map((comp) => {
        const bounds = getComponentPixelBounds(comp);
        return (
          <div
            key={comp.id}
            style={{
              position: 'absolute',
              left: bounds.x,
              top: bounds.y,
              width: bounds.width,
              height: bounds.height,
            }}
          >
            <TemplateComponentContent
              type={comp.type}
              garment={garmentData}
              width={bounds.width}
              height={bounds.height}
            />
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// TEMPLATE COMPONENT RENDERER
// ============================================================================

interface TemplateComponentContentProps {
  type: ComponentType;
  garment: LabelGarmentData;
  width: number;
  height: number;
}

/**
 * Render component content for print output.
 * Handles all 14 component types.
 *
 * CRITICAL: Uses inline styles only — NO Tailwind classes.
 * The print window (usePrintLabels) opens a new browser window via window.open()
 * and injects raw HTML via document.write(). That window does NOT have Tailwind CSS
 * loaded, so any Tailwind utility classes would be silently ignored, causing the
 * print output to look completely different from the editor preview.
 */
function TemplateComponentContent({
  type,
  garment,
  width,
  height,
}: TemplateComponentContentProps) {
  const { t } = useTranslation('settings');
  const minDim = Math.min(width, height);
  const baseFontSize = Math.max(10, Math.min(14, height * 0.3));
  const smallFontSize = Math.max(9, baseFontSize * 0.8);

  // Build QR payload. Phase 50.01.3 / BLU-233: encodes V2; `m` is driven off the
  // garment's real print_technology (threaded through GarmentData), default DTG.
  const printTechnology = garment.printTechnology ?? 'DTG';
  const qrPayload: GarmentQRPayload = {
    v: CURRENT_QR_VERSION,
    g: garment.id,
    o: garment.orderNumber,
    s: garment.sku,
    m: printTechnology,
    t: garment.fulfillmentType,
  };
  const encodedQR = encodeGarmentQR(qrPayload);

  // Use original Shopify SKU for barcode (full decorated SKU), fallback to normalized SKU
  const barcodeSku = garment.shopifySku || garment.sku;
  const barcodeValue = isValidBarcodeContent(barcodeSku)
    ? barcodeSku
    : sanitizeBarcodeContent(barcodeSku);

  // Shared inline style bases (replaces Tailwind utility classes)
  const fullSize: React.CSSProperties = { width: '100%', height: '100%', boxSizing: 'border-box' };
  const flexCenter: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const whiteBg: React.CSSProperties = { backgroundColor: '#ffffff' };
  const blackBorder: React.CSSProperties = { border: '1px solid #000000' };
  const bold: React.CSSProperties = { fontWeight: 'bold' };
  const truncate: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  switch (type) {
    case 'qr':
      return (
        <div style={{ ...fullSize, ...flexCenter, ...whiteBg }}>
          <QRCodeSVG
            value={encodedQR}
            size={minDim * 0.9}
            level="M"
            includeMargin={false}
          />
        </div>
      );

    case 'barcode':
      return (
        <div style={{ ...fullSize, ...flexCenter, ...whiteBg, overflow: 'hidden' }}>
          <Barcode
            value={barcodeValue}
            format="CODE128"
            width={Math.max(1, width / 80)}
            height={Math.max(20, height * 0.6)}
            fontSize={Math.max(6, height * 0.15)}
            margin={0}
            displayValue={true}
            background="#ffffff"
            lineColor="#000000"
          />
        </div>
      );

    case 'orderNumberBarcode': {
      // Order number barcode - CODE128 encoding the order number for shipping scan.
      // Stripped of any leading '#' so it matches the invoice barcode's payload.
      const orderNum = stripOrderNamePrefix(garment.orderNumber);
      if (!orderNum) return null;
      const orderBarcodeValue = isValidBarcodeContent(orderNum)
        ? orderNum
        : sanitizeBarcodeContent(orderNum);
      if (!orderBarcodeValue) return null;
      return (
        <div style={{ ...fullSize, ...flexCenter, ...whiteBg, overflow: 'hidden' }}>
          <Barcode
            value={orderBarcodeValue}
            format="CODE128"
            width={Math.max(1, width / 80)}
            height={Math.max(20, height * 0.6)}
            fontSize={Math.max(6, height * 0.15)}
            margin={0}
            displayValue={false}
            background="#ffffff"
            lineColor="#000000"
          />
        </div>
      );
    }

    case 'orderNumber':
      return (
        <div style={{ ...fullSize, ...flexCenter, ...whiteBg, ...blackBorder, ...bold, fontSize: baseFontSize }}>
          #{formatOrderNumber(garment.orderNumber)}
        </div>
      );

    case 'sizeColor':
      return (
        <div style={{ ...fullSize, ...flexCenter, ...whiteBg, ...blackBorder, ...bold, fontSize: baseFontSize }}>
          <span style={{ ...truncate, paddingLeft: 4, paddingRight: 4 }}>
            {garment.size} | {garment.color.toUpperCase()}
          </span>
        </div>
      );

    case 'productInfo':
      return (
        <div style={{ ...fullSize, ...whiteBg, ...blackBorder, padding: 4, overflow: 'hidden', fontSize: smallFontSize }}>
          <div style={{ ...bold, overflow: 'hidden', lineHeight: 1.2, wordBreak: 'break-word' }}>{garment.productName}</div>
          <div style={{ ...truncate, color: '#4b5563' }}>{garment.sku}</div>
        </div>
      );

    case 'itemCount':
      return (
        <div style={{ ...fullSize, ...flexCenter, ...whiteBg, ...blackBorder, fontSize: smallFontSize }}>
          {t('production.labelContent.itemOf', { item: garment.itemNumber, total: garment.totalItems })}
        </div>
      );

    case 'binLabel':
      if (!garment.binLabel) return null;
      return (
        <div style={{ ...fullSize, ...flexCenter, ...whiteBg, ...blackBorder, ...bold, fontSize: baseFontSize }}>
          <span style={{ ...truncate, paddingLeft: 4, paddingRight: 4 }}>&rarr; {garment.binLabel}</span>
        </div>
      );

    case 'productType':
      if (!garment.shopifyProductType) return null;
      return (
        <div style={{ ...fullSize, ...flexCenter, ...whiteBg, ...blackBorder, fontSize: smallFontSize }}>
          <span style={{ ...truncate, paddingLeft: 4, paddingRight: 4 }}>{garment.shopifyProductType}</span>
        </div>
      );

    case 'printArea': {
      // Phase 104 (D-09): where each print goes on a manual order's garment.
      // Nothing (no empty box) when the unit has no print areas.
      const lines = garment.printArea ?? [];
      if (lines.length === 0) return null;
      // Shrink to fit, then wrap: a cut-short area line is a misprint.
      const lineFontSize = printAreaFontSize({ lines, width, height, base: baseFontSize, min: 8 });
      return (
        <div
          data-testid="label-print-area"
          style={{
            ...fullSize,
            ...whiteBg,
            ...blackBorder,
            ...bold,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            paddingLeft: 4,
            paddingRight: 4,
            lineHeight: 1.15,
            fontSize: lineFontSize,
            overflow: 'hidden',
          }}
        >
          {lines.map((line) => (
            <div key={line} style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              {line}
            </div>
          ))}
        </div>
      );
    }

    case 'checkboxes':
      return (
        <div style={{ ...fullSize, display: 'flex', ...whiteBg, gap: 2 }}>
          {(['front', 'back'] as const).map((side) => (
            <div
              key={side}
              style={{ flex: '1 1 0%', ...blackBorder, ...flexCenter, ...bold, fontSize: baseFontSize }}
            >
              {t(`production.labelContent.${side}`)}
            </div>
          ))}
        </div>
      );

    case 'dtgIndicator': {
      // Phase 50.01.3 / BLU-233: print-method indicator driven off the garment's
      // print_technology. DTF gets a distinct semantic colour (indigo) so it
      // never reads as a DTG black box on the shop floor.
      const isDtf = (garment.printTechnology ?? 'DTG') === 'DTF';
      return (
        <div
          data-testid="production-method-indicator"
          style={{
            ...fullSize,
            ...flexCenter,
            ...bold,
            fontSize: smallFontSize,
            backgroundColor: isDtf ? LABEL_COLORS.DTF_INDICATOR_BG : LABEL_COLORS.BORDER,
            color: isDtf ? LABEL_COLORS.DTF_INDICATOR_TEXT : '#ffffff',
          }}
        >
          {isDtf ? 'DTF' : 'DTG'}
        </div>
      );
    }

    case 'ptBadge':
      if (!garment.requiresPretreat) return null;
      return (
        <div
          style={{
            ...fullSize,
            ...flexCenter,
            ...bold,
            fontSize: smallFontSize,
            backgroundColor: LABEL_COLORS.PRETREAT_BADGE_BG,
            color: LABEL_COLORS.PRETREAT_BADGE_TEXT,
          }}
        >
          PT
        </div>
      );

    case 'redoBadge':
      if (!garment.isRedo) return null;
      return (
        <div
          style={{
            ...fullSize,
            ...flexCenter,
            ...bold,
            fontSize: smallFontSize,
            backgroundColor: LABEL_COLORS.REDO_BADGE,
            color: '#ffffff',
          }}
        >
          REDO
        </div>
      );

    default:
      return null;
  }
}

// ============================================================================
// LEGACY LABEL (PRESERVED FOR BACKWARDS COMPATIBILITY)
// ============================================================================

interface LegacyGarmentLabelProps {
  garment: GarmentLabelProps['garment'];
  binLabel?: string;
  isRedo: boolean;
  redoReason?: string;
}

/**
 * Legacy hardcoded layout - preserved for backwards compatibility.
 * This is the original GarmentLabel implementation.
 * Can be removed once all templates are migrated and verified.
 */
function LegacyGarmentLabel({
  garment,
  binLabel,
  isRedo,
  redoReason,
}: LegacyGarmentLabelProps) {
  const { t } = useTranslation('settings');
  // Build QR payload. Phase 50.01.3 / BLU-233: encodes V2; `m` driven off the
  // garment's real print_technology (default DTG).
  const legacyPrintTechnology = garment.printTechnology ?? 'DTG';
  const qrPayload: GarmentQRPayload = {
    v: CURRENT_QR_VERSION,
    g: garment.id,
    o: garment.orderNumber,
    s: garment.sku,
    m: legacyPrintTechnology,
    t: garment.fulfillmentType,
  };

  const encodedQR = encodeGarmentQR(qrPayload);

  // Use original Shopify SKU for barcode (full decorated SKU), fallback to normalized SKU
  const barcodeSku = garment.shopifySku || garment.sku;
  const barcodeValue = isValidBarcodeContent(barcodeSku)
    ? barcodeSku
    : sanitizeBarcodeContent(barcodeSku);

  return (
    <div
      className="label"
      data-testid="garment-label"
      data-garment-id={garment.id}
      data-layout="legacy"
      style={{
        width: LABEL_DIMENSIONS.WIDTH,
        height: LABEL_DIMENSIONS.HEIGHT,
        padding: `${LABEL_DIMENSIONS.PADDING_PX}px`,
        border: `2px solid ${LABEL_COLORS.BORDER}`,
        display: 'grid',
        gridTemplateColumns: `${LABEL_DIMENSIONS.QR_COLUMN_WIDTH} 1fr`,
        gridTemplateRows: 'auto 1fr auto',
        gap: '8px',
        fontFamily: '"PingFang SC", "Microsoft YaHei", "Noto Sans SC", Arial, "Helvetica Neue", sans-serif',
        backgroundColor: LABEL_COLORS.BACKGROUND,
        boxSizing: 'border-box',
      }}
    >
      {/* Left Column - QR Code (for Production App) */}
      <div
        style={{
          gridRow: '1 / 4',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {/* QR Code */}
        <QRCodeSVG
          data-testid="garment-qr-code"
          value={encodedQR}
          size={QR_CONFIG.SIZE_PX}
          level={QR_CONFIG.ERROR_CORRECTION}
          style={{ flexShrink: 0 }}
        />

        {/* F B checkboxes for front/back print */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {(['front', 'back'] as const).map((side) => (
            <div
              key={side}
              data-testid={`checkbox-${side}`}
              style={{
                flex: 1,
                border: `2px solid ${LABEL_COLORS.BORDER}`,
                padding: '6px',
                textAlign: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              {t(`production.labelContent.${side}`)}
            </div>
          ))}
        </div>

        {/* Print-method indicator (Phase 50.01.3 / BLU-233): DTG black box vs
            DTF indigo box, driven off the garment's print_technology. */}
        <div style={{ display: 'flex', gap: '4px' }}>
          <div
            data-testid="production-method-indicator"
            style={{
              flex: 1,
              border: `2px solid ${legacyPrintTechnology === 'DTF' ? LABEL_COLORS.DTF_INDICATOR_BG : LABEL_COLORS.BORDER}`,
              padding: '4px',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              backgroundColor: legacyPrintTechnology === 'DTF' ? LABEL_COLORS.DTF_INDICATOR_BG : LABEL_COLORS.BORDER,
              color: legacyPrintTechnology === 'DTF' ? LABEL_COLORS.DTF_INDICATOR_TEXT : LABEL_COLORS.BACKGROUND,
            }}
          >
            {legacyPrintTechnology === 'DTF' ? 'DTF' : 'DTG'}
          </div>
        </div>
      </div>

      {/* Top Right - Order Number + Size/Color */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '8px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {/* Redo badge */}
          {isRedo && (
            <div
              data-testid="redo-badge"
              style={{
                backgroundColor: LABEL_COLORS.REDO_BADGE,
                color: LABEL_COLORS.BACKGROUND,
                padding: '4px 8px',
                fontWeight: 'bold',
                fontSize: '12px',
              }}
            >
              REDO
            </div>
          )}
          {/* Order number */}
          <div
            data-testid="order-number"
            style={{
              border: `2px solid ${LABEL_COLORS.BORDER}`,
              padding: '6px 12px',
              fontWeight: 'bold',
              fontSize: '14px',
            }}
          >
            #{formatOrderNumber(garment.orderNumber)}
          </div>
        </div>

        {/* Size | Color badge */}
        <div
          data-testid="size-color-badge"
          style={{
            border: `2px solid ${LABEL_COLORS.BORDER}`,
            padding: '8px 12px',
            fontSize: '18px',
            fontWeight: 'bold',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {garment.size} | {garment.color.toUpperCase()}
        </div>
      </div>

      {/* Middle Right - Product Info */}
      <div
        style={{
          border: `2px solid ${LABEL_COLORS.BORDER}`,
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <div
          data-testid="product-name"
          style={{ fontWeight: 'bold', fontSize: '12px' }}
        >
          {garment.productName}
        </div>
        <div
          data-testid="product-sku"
          style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}
        >
          {garment.sku}
        </div>
        {/* Item count if part of multi-line order */}
        {garment.itemNumber && garment.totalItems && (
          <div
            data-testid="item-count"
            style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}
          >
            {t('production.labelContent.itemOf', { item: garment.itemNumber, total: garment.totalItems })}
          </div>
        )}
        {/* Bin label for multi-line P&P */}
        {binLabel && (
          <div
            data-testid="bin-label"
            style={{ marginTop: '8px', fontWeight: 'bold', fontSize: '14px' }}
          >
            &rarr; {binLabel}
          </div>
        )}
        {/* Redo reason */}
        {isRedo && redoReason && (
          <div
            data-testid="redo-reason"
            style={{
              marginTop: '4px',
              fontSize: '10px',
              color: LABEL_COLORS.REDO_BADGE,
            }}
          >
            {redoReason === PRINT_TECHNOLOGY_SWITCH_REASON
              ? t('production.labelContent.switchedTo', { tech: legacyPrintTechnology })
              : t('production.labelContent.original', {
                  reason: redoReason === CLIENT_QC_REDO_REASON ? t('production.labelContent.clientQc') : redoReason,
                })}
          </div>
        )}
      </div>

      {/* Bottom Right - Barcode for AutoPrint + PT indicator */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
        }}
      >
        {/* Pretreat indicator */}
        {garment.requiresPretreat && (
          <div
            data-testid="pretreat-badge"
            style={{
              backgroundColor: LABEL_COLORS.PRETREAT_BADGE_BG,
              color: LABEL_COLORS.PRETREAT_BADGE_TEXT,
              padding: '8px 12px',
              fontWeight: 'bold',
              fontSize: '14px',
            }}
          >
            PT
          </div>
        )}

        {/* Barcode (bottom-right for AutoPrint) */}
        <div data-testid="garment-barcode" style={{ marginLeft: 'auto' }}>
          <Barcode
            value={barcodeValue}
            format={BARCODE_CONFIG.format}
            width={BARCODE_CONFIG.width}
            height={BARCODE_CONFIG.height}
            fontSize={BARCODE_CONFIG.fontSize}
            margin={BARCODE_CONFIG.margin}
            displayValue={BARCODE_CONFIG.displayValue}
            background={BARCODE_CONFIG.background}
            lineColor={BARCODE_CONFIG.lineColor}
          />
        </div>
      </div>
    </div>
  );
}

export default GarmentLabel;
